import { useState } from "react";
import { Check, Layers, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useClasificarCaptura, useDevolverCapturaASinClasificar } from "@/hooks/useClasificarCaptura";
import { getApiErrorMessage } from "@/lib/api";
import type { DestinoDeCaptura, RegistroDeTerreno, SeccionDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import {
  filtrarOpciones,
  mostrarBuscador,
  opcionesDeLaSeccion,
  resumenDeLaSeleccion,
  seccionesClasificables,
} from "./clasificarCapturas";
import { horaDeCaptura, tiposDelRegistro } from "./registroDeTerreno";

/**
 * El selector de dos pasos con que se clasifica una captura rápida desde la web
 * (diseño «Editor de informe», Registro de terreno): «1 · Sección» y, elegida,
 * «2 · Asignar a». Todo lo que se puede elegir sale de `clasificarCapturas.ts`.
 *
 * EN LÍNEA, DENTRO DE LA TARJETA y no en un modal: quien clasifica tiene la
 * captura (foto, texto, audio) a la vista mientras decide dónde va.
 *
 * El panel SOLO GUARDA al apretar «Guardar»; si el servidor lo rechaza el error
 * se dice acá y el panel sigue abierto, con lo elegido.
 */
function SelectorDeDestino({
  secciones,
  inicial,
  guardando,
  error,
  onGuardar,
  onCancelar,
}: {
  secciones: SeccionDeTerreno[];
  inicial: DestinoDeCaptura | null;
  guardando: boolean;
  error: string | null;
  onGuardar: (destino: DestinoDeCaptura) => void;
  onCancelar: () => void;
}) {
  const [destino, setDestino] = useState<string | null>(inicial?.destino ?? null);
  const [itemRef, setItemRef] = useState<string | null>(inicial?.item_ref ?? null);
  const [busqueda, setBusqueda] = useState("");

  const clasificables = seccionesClasificables(secciones);
  const seccion = clasificables.find((s) => s.clave === destino);
  const opciones = seccion ? opcionesDeLaSeccion(seccion) : [];
  const visibles = filtrarOpciones(opciones, busqueda);

  function elegirSeccion(clave: string) {
    if (clave === destino) return;
    setDestino(clave);
    setItemRef(null);
    setBusqueda("");
  }

  return (
    <div className="border-divisor mt-1 flex flex-col gap-3 border-t pt-3">
      <div className="flex flex-col gap-2">
        <Rotulo>1 · Sección</Rotulo>
        <div role="radiogroup" aria-label="Sección" className="flex flex-wrap gap-1.5">
          {clasificables.map((s) => (
            <button
              key={s.clave}
              type="button"
              role="radio"
              aria-checked={s.clave === destino}
              onClick={() => elegirSeccion(s.clave)}
              className={cn(
                "focus-visible:ring-ring/50 h-[1.875rem] rounded-md border-[1.5px] px-3 text-xs outline-none focus-visible:ring-[3px]",
                s.clave === destino
                  ? "border-primary bg-accent text-ink font-bold"
                  : "border-border text-ink-body hover:bg-muted font-semibold",
              )}
            >
              {s.nombre}
            </button>
          ))}
        </div>
      </div>

      {seccion && (
        <div className="flex flex-col gap-2">
          <Rotulo>2 · Asignar a</Rotulo>
          <p className="text-ink-muted text-xs font-semibold">
            Toda la sección o un elemento puntual de {seccion.nombre}.
          </p>

          {mostrarBuscador(opciones) && (
            <div className="relative">
              <Search
                aria-hidden="true"
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2"
              />
              <input
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar…"
                aria-label="Buscar dónde asignar"
                className="border-border bg-background text-ink placeholder:text-ink-hint focus-visible:ring-ring/50 h-9 w-full rounded-md border-[1.5px] pr-3 pl-8 text-xs font-semibold outline-none focus-visible:ring-[3px]"
              />
            </div>
          )}

          {visibles.length === 0 ? (
            <p className="text-ink-hint text-xs font-semibold">Sin resultados.</p>
          ) : (
            <div
              role="radiogroup"
              aria-label="Asignar a"
              className="flex max-h-56 flex-col gap-1.5 overflow-y-auto"
            >
              {visibles.map((o) => {
                const elegida = o.referencia === itemRef;

                return (
                  <button
                    key={o.referencia ?? "seccion-completa"}
                    type="button"
                    role="radio"
                    aria-checked={elegida}
                    onClick={() => setItemRef(o.referencia)}
                    className={cn(
                      "focus-visible:ring-ring/50 flex items-center gap-2.5 rounded-md border-[1.5px] px-3 py-2 text-left text-xs outline-none focus-visible:ring-[3px]",
                      elegida ? "border-primary bg-accent" : "border-border hover:bg-muted",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex size-3.5 flex-none items-center justify-center rounded-full border-[1.5px]",
                        elegida ? "border-primary" : "border-ink-hint",
                      )}
                    >
                      {elegida && <span className="bg-primary size-1.5 rounded-full" />}
                    </span>
                    <span className={cn("text-ink min-w-0 flex-1 truncate", elegida ? "font-bold" : "font-semibold")}>
                      {o.nombre}
                    </span>
                    <span className="text-ink-muted flex-none text-[0.6875rem] font-semibold">{o.tipo}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div aria-live="polite">
        {error ? (
          <p role="alert" className="text-destructive text-xs font-medium">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span data-resumen className="text-ink-body min-w-0 flex-1 text-xs font-bold">
          {resumenDeLaSeleccion(secciones, destino, itemRef)}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onCancelar} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={destino === null || guardando}
          onClick={() => destino !== null && onGuardar({ destino, item_ref: itemRef })}
        >
          {guardando && <Spinner />}
          Guardar
        </Button>
      </div>
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-ink-muted text-[0.59375rem] font-extrabold tracking-[0.07em] uppercase">
      {children}
    </span>
  );
}

const MENSAJE_AL_GUARDAR = "No pudimos guardar la clasificación. Inténtelo de nuevo.";

/**
 * El pie de una captura SIN CLASIFICAR: el botón «Clasificar» y, abierto, el
 * selector. Las que clasificó la App nunca lo reciben.
 */
export function ClasificadorDeCaptura({
  informeId,
  uuid,
  secciones,
}: {
  informeId: number;
  uuid: string;
  secciones: SeccionDeTerreno[];
}) {
  const clasificar = useClasificarCaptura(informeId);
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) {
    return (
      <div className="flex">
        <Button type="button" size="sm" onClick={() => setAbierto(true)}>
          <Layers aria-hidden="true" />
          Clasificar
        </Button>
      </div>
    );
  }

  return (
    <SelectorDeDestino
      secciones={secciones}
      inicial={null}
      guardando={clasificar.isPending}
      error={error}
      onCancelar={() => {
        setError(null);
        setAbierto(false);
      }}
      onGuardar={(destino) => {
        setError(null);
        clasificar.mutate(
          { uuid, ...destino },
          {
            onSuccess: () => toast.success("Captura clasificada."),
            onError: (e) => setError(getApiErrorMessage(e, MENSAJE_AL_GUARDAR)),
          },
        );
      }}
    />
  );
}

/**
 * Una captura que la web ya clasificó: fila compacta con su destino y las tres
 * salidas — verla donde quedó, cambiarla o devolverla a «sin clasificar».
 */
export function FilaClasificadaEnLaWeb({
  informeId,
  registro,
  secciones,
  onVer,
}: {
  informeId: number;
  registro: RegistroDeTerreno;
  secciones: SeccionDeTerreno[];
  onVer: (clave: string) => void;
}) {
  const clasificar = useClasificarCaptura(informeId);
  const devolver = useDevolverCapturaASinClasificar(informeId);
  const [cambiando, setCambiando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clasificacion = registro.clasificacion;
  const tipos = tiposDelRegistro(registro);

  return (
    <div
      data-testid={`clasificada-${registro.uuid}`}
      className="border-divisor bg-success-soft/40 flex flex-col gap-2 border-t px-3.5 py-2.5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Check className="text-success-soft-foreground size-4 flex-none" aria-hidden="true" />
        <span className="text-ink text-xs font-bold">
          Captura {horaDeCaptura(registro.capturado_en)}
          {tipos ? ` · ${tipos}` : ""}
        </span>
        <span className="text-success-soft-foreground min-w-0 text-xs font-bold">
          → {clasificacion?.destino_nombre ?? "Sin destino"}
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-3">
          {clasificacion?.destino && (
            <AccionDeTexto onClick={() => onVer(clasificacion.destino!)}>Ver</AccionDeTexto>
          )}
          <AccionDeTexto onClick={() => setCambiando((v) => !v)}>Cambiar</AccionDeTexto>
          <AccionDeTexto
            disabled={devolver.isPending}
            onClick={() => {
              setError(null);
              devolver.mutate(registro.uuid, {
                onSuccess: () => toast.success("Captura devuelta a sin clasificar."),
                onError: (e) =>
                  setError(getApiErrorMessage(e, "No pudimos devolver la captura. Inténtelo de nuevo.")),
              });
            }}
          >
            Devolver a sin clasificar
          </AccionDeTexto>
        </span>
      </div>

      {error && !cambiando ? (
        <p role="alert" className="text-destructive text-xs font-medium">
          {error}
        </p>
      ) : null}

      {cambiando && (
        <SelectorDeDestino
          secciones={secciones}
          inicial={
            clasificacion?.destino
              ? { destino: clasificacion.destino, item_ref: clasificacion.item_ref }
              : null
          }
          guardando={clasificar.isPending}
          error={error}
          onCancelar={() => {
            setError(null);
            setCambiando(false);
          }}
          onGuardar={(destino) => {
            setError(null);
            clasificar.mutate(
              { uuid: registro.uuid, ...destino },
              {
                onSuccess: () => {
                  toast.success("Captura clasificada.");
                  setCambiando(false);
                },
                onError: (e) => setError(getApiErrorMessage(e, MENSAJE_AL_GUARDAR)),
              },
            );
          }}
        />
      )}
    </div>
  );
}

function AccionDeTexto({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-success-soft-foreground hover:text-ink focus-visible:ring-ring/50 rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px] disabled:opacity-50"
    >
      {children}
    </button>
  );
}
